import { ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useResponse } from 'contexts/response';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Location } from 'types';
import { isLocation } from 'utils';

const useLocationApi = () => {
  const { handleError, setSuccess } = useResponse();
  const params = useParams();

  const handleFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const response = await fetch(input, init);
        if (!response.ok) {
          handleError(response);
          return null;
        }
        const json = await response.json();
        if (isLocation(json)) {
          setSuccess(
            <dl>
              <dt className="text-info-main">LOCATION</dt>
              <dd className="text-info-dark">{json.name}</dd>
              <dt className="text-info-main">CODE</dt>
              <dd className="text-info-dark">{json.code}</dd>
              <dt className="text-info-main"> CODE</dt>
              <dd className="text-info-dark">{json.code}</dd>
              <dt className="text-info-main"> ADDRESS</dt>
              <dd className="text-info-dark">{json.address}</dd>
              <dt className="text-info-main"> ADDRESS 2</dt>
              <dd className="text-info-dark">{json.address2}</dd>
              <dt className="text-info-main"> ZIP CODE</dt>
              <dd className="text-info-dark">{json.zipCode}</dd>
              <dt className="text-info-main"> COUNTRY</dt>
              <dd className="text-info-dark">{json.country}</dd>
              <dt className="text-info-main"> CITY</dt>
              <dd className="text-info-dark">{json.city}</dd>
              <dt className="text-info-main"> STATE</dt>
              <dd className="text-info-dark">{json.state}</dd>
              <dt className="text-info-main"> TIMEZONE</dt>
              <dd className="text-info-dark">{json.timeZone}</dd>
              <dt className="text-info-main"> UTC OFFSET</dt>
              <dd className="text-info-dark">{json.utcOffset}</dd>
            </dl>
          );
          return json;
        }
        await handleError(response, json);
        return null;
      } catch (e) {
        await handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const createLocation = useCallback(
    async (location: Partial<Location>): Promise<Location | null> => {
      const init: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(location)
      };
      const input = new URL('/api/locations', window.location.origin);
      return await handleFetch(input, init);
    },
    [handleFetch]
  );

  const editLocation = useCallback(
    async (location?: Location | null): Promise<Location | null> => {
      if (!isLocation(location)) {
        handleError('Invalid location');
        return null;
      }
      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(location)
      };
      const input = new URL(`/api/locations/${location.id}`, window.location.origin);
      return await handleFetch(input, init);
    },
    [handleError, handleFetch]
  );

  const deleteLocation = useCallback(
    async (id: number, deleted: boolean) => {
      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(deleted)
      };
      const input = new URL(`/api/locations/${id}/deleted`, window.location.origin);

      try {
        const response = await fetch(input, init);
        if (!response.ok) {
          handleError(response);
          return null;
        }
        setSuccess(`Location #${id} ${deleted ? 'deactivated' : 'reactivated'}`);
        return true;
      } catch (e) {
        handleError(e);
      }
    },
    [handleError, setSuccess]
  );

  const getLocation = useCallback(
    async (id: number | string): Promise<Location | null> => {
      const init: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const input = new URL(`/api/locations/${id}`, window.location.origin);

      try {
        const response = await fetch(input, init);
        if (!response.ok) {
          handleError(response);
          return null;
        }
        const json = await response.json();
        if (response.ok && isLocation(json)) {
          return json;
        }
      } catch (e) {
        handleError(e);
      }
      return null;
    },
    [handleError]
  );

  const validateCodeUniqueness = useCallback(
    async (code: string): Promise<ValidationResult> => {
      if (!code) return { status: ValidationStatus.DEFAULT };

      const init: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const input = new URL(`/api/locations?code=${code}`, window.location.origin);

      try {
        const response = await fetch(input, init);
        if (!response.ok) {
          return { status: ValidationStatus.WARNING, error: response.statusText };
        }
        const json = await response.json();
        if (response.ok && isLocation(json)) {
          if (json.id === Number(params.id)) return { status: ValidationStatus.DEFAULT };
          return { status: ValidationStatus.WARNING, error: `Location code ${code} already used (${json.name})` };
        }
        return { status: ValidationStatus.SUCCESS };
      } catch (_) {
        return { status: ValidationStatus.DEFAULT };
      }
    },
    [params.id]
  );

  const searchLocations = useCallback(async (value: string, filterByUserAssociations = true): Promise<Location[]> => {
    const url = new URL('/api/locations/search', window.location.origin);
    url.searchParams.append('filterString', `(code=="*${value}*",name=="*${value}*")`);
    url.searchParams.append('filterByUserAssociations', filterByUserAssociations.toString());

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const json = await response.json();
        if (Array.isArray(json)) return json;
      }
    } catch (e) {
      console.error(e);
    }

    return [];
  }, []);

  return { createLocation, deleteLocation, editLocation, getLocation, searchLocations, validateCodeUniqueness };
};

export default useLocationApi;
